import React, { useMemo, useState, useEffect, lazy, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FormProvider, useForm } from "react-hook-form";
import { getStoragekeysNegociations } from "~pages/PricesTableV2/helpers/storage";
import FiltroPrecoCliente from "~pages/PricesTableV2/components/filters/FiltroPrecoCliente";
import useCliente from "~/fetchs/useCliente";
import useModal from "~components/Modal/hooks/useModal";
import usePrecoRegra from "~fetchs/usePrecoRegraV2";
import useTabelaPrecoCliente from "~/fetchs/useTabelaPrecoCliente";
import useUnsavedChangesWarningTable from "~components/Form/hooks/useUnsavedChangesWarningTable";
import { TableStateProvider, useTableState } from "~pages/PricesTableV2/context/TableStateContext";
import { renovarNegociacao } from "~pages/PricesTableV2/hooks/PricesTableServices";
import apiGenericClient from "~services/utils/apiGenericClient";
import { useConfigureFooter } from "~hooks/useHelpers";
import TabelaDePrecoSubmit from "~pages/PricesTableV2/components/Footer/TabelaDePrecoSubmit";
import ContainerDoPrecoCliente from "~pages/PricesTableV2/components/Tabela/ContainerDoPrecoCliente";
import ResponseAlert from "~components/Form/components/ResponseAlert";
import LazyModalWrapper from "~pages/PricesTableV2/components/Modal/LazyModalWrapper";

import LoaderFull from "~components/Form/components/LoaderFull";
import { initialPath } from "~misc/paths";
import { getCodigoExternos } from "~services/GlobalServices";
import PricesTableSolicitarNegociacaoModal from "~pages/PricesTableV2/components/Modal/SolicitarNegociacaoModal";
import InformacoesAdicionaisModal from "~pages/PricesTableV2/components/Modal/InformacoesAdicionaisModal";
import ExcluirNegociacao from "~pages/PricesTableV2/components/Modal/ExcluirNegociacao";
import ModalResponse from "~components/Modal/ModalResponse";
import ModalErroNegociacao from "~pages/PricesTableV2/components/Modal/ModalErroNegociacao";
import { SaveIndexBD } from "~services/utils/SaveIndexBD";

const ClientPriceContent = () => {
  const take = 200;
  const { id } = useParams();
  const navigate = useNavigate();
  const { cliente } = useCliente(id);
  const { isFormDirty, clearAll, alteracoes } = useTableState();

  useEffect(() => {
    clearAll();
  }, [clearAll]);

  const [showFooterButtons, setShowFooterButtons] = useState(false);
  const [loading, setLoading] = useState(false);
  const [loadingFilters, setLoadingFilters] = useState(false);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [aplicaServicos, setAplicaServicos] = useState({ descarga: false, fracionamento: false });
  const [isOpenInformacoesAdicionaisAlteracaoModal, setIsOpenInformacoesAdicionaisAlteracaoModal] = useState(false);
  const [showResponseAlert, setShowResponseAlert] = useState(false);
  const [showResponseAlertError, setShowResponseAlertError] = useState(false);
  const [showResponseErrorMsg, setShowResponseErrorMsg] = useState('');
  const [modalErros, setModalErros] = useState([]);
  const [showResponseMsg, setShowResponseMsg] = useState(
    "Condições comerciais enviadas com sucesso para portal com motivo de ALTERAÇÃO DE FAIXA",
  );
  const [skip, setSkip] = useState(0);
  const [dadosHeader, setDadosHeader] = useState({});
  const [dadosDelete, setDadosDelete] = useState(null);
  const [precosRegraData, setPrecosRegraData] = useState(null);
  const [responseDelete, setResponseDelete] = useState({
    isOpen: false,
    message: "Envio de exclusão feito com sucesso!",
    link: null,
  });
  const { isModalOpen, closeModal, setModalOpen } = useModal();
  const {
    isModalOpen: isExcluirModalOpenPreco,
    closeModal: closeExcluirModalPreco,
    setModalOpen: setExcluirModalOpenPreco,
  } = useModal();

  const keys = useMemo(() => getStoragekeysNegociations("clientePrice"), []);

  const getDefaultValues = () => {
    try {
      const savedProduto = localStorage.getItem(keys.produto);
      const savedGrupoProduto = localStorage.getItem(keys.grupoProduto);
      const savedFilial = localStorage.getItem(keys.filiais);
      const savedNegociacao = localStorage.getItem(keys.negociacao);
      const savedPeriodo = localStorage.getItem(keys.periodo);
      const savedMedida = localStorage.getItem(keys.medida);

      return {
        cliente: null,
        grupoProduto: savedGrupoProduto ? JSON.parse(savedGrupoProduto).value : 0,
        produto: savedProduto ? JSON.parse(savedProduto).value : 0,
        filial: savedFilial ? JSON.parse(savedFilial).value : 0,
        tipoSolicitacao: savedNegociacao ? JSON.parse(savedNegociacao).value : 0,
        ordem: "recentes",
        statusSolicitacao: 0,
        periodo: savedPeriodo ? JSON.parse(savedPeriodo) : { value: 0, label: "Todos", startDate: null, endDate: null },
        unidadeMedida: savedMedida ? JSON.parse(savedMedida).value : 0,
      };
    } catch (error) {
      console.warn("Error loading filters from localStorage:", error);
      return {
        cliente: null,
        grupoProduto: 0,
        produto: 0,
        filial: 0,
        tipoSolicitacao: 0,
        ordem: "recentes",
        statusSolicitacao: 0,
        periodo: { value: 0, label: "Todos", startDate: null, endDate: null },
        medida: 0,
      };
    }
  };

  const methods = useForm({
    defaultValues: getDefaultValues(),
  });

  const grupoProduto = methods.watch("grupoProduto");
  const produto = methods.watch("produto");
  const filial = methods.watch("filial");
  const tipoSolicitacao = methods.watch("tipoSolicitacao");
  const ordem = methods.watch("ordem");
  const periodo = methods.watch("periodo");
  const unidadeMedida = methods.watch("unidadeMedida");

  const filtersWatch = useMemo(
    () => ({
      cliente: null,
      grupoProduto: grupoProduto || 0,
      produto: produto || 0,
      filial: filial || 0,
      tipoSolicitacao: tipoSolicitacao || 0,
      ordem: ordem || -1,
      periodo: periodo?.value || 0,
      startDate: periodo?.startDate || null,
      endDate: periodo?.endDate || null,
      unidadeMedida: unidadeMedida || 0,
    }),
    [grupoProduto, produto, filial, tipoSolicitacao, ordem, periodo, unidadeMedida],
  );

  const { filtrosDisponiveisPrecoRegra } = usePrecoRegra({
    filters: filtersWatch,
    microrregiao: cliente?.microrregiaoId,
    cluster: cliente?.clusterId,
    setLoading: setLoadingFilters,
  });

  const { tabelaPrecoClientesFormatada, total, totalRenderizado } = useTabelaPrecoCliente({
    filters: filtersWatch,
    setLoading: setLoadingSearch,
    clienteId: id,
    codigoCliente: cliente?.codigo,
    microrregiao: cliente?.microrregiaoId,
    cluster: cliente?.clusterId,
    cidadeId: cliente?.endereco?.cidade?.id,
    domicilioFiscal: cliente?.domicilioFiscal,
    skip,
    take,
  });

  const handleAltualizar = () => {
    setIsOpenInformacoesAdicionaisAlteracaoModal(true);
  };

  const handleChangeCheckbox = useCallback(() => {
    const anyChecked = (document.querySelectorAll(".checkbox-column input[type='checkbox']:checked") || []).length > 0;
    setShowFooterButtons(anyChecked);
  }, []);

  const handleAddButton = (grupoProdutoId, produtoId, filialId, clienteId, origem) => {
    const dadosHeader = { grupoProdutoId, produtoId, filialId, clienteId, origem };

    if (origem === "editar" && clienteId) {
      SaveIndexBD("dados-selecionados-negociacao", dadosHeader);
      navigate(`${initialPath}/tabela-preco/${clienteId}/editar-negociacao`);
      return;
    }

    if (origem === "nova" && clienteId) {
      SaveIndexBD("dados-selecionados-negociacao", dadosHeader);
      navigate(`${initialPath}/tabela-preco/${clienteId}/nova-negociacao`);
      return;
    }

    setModalOpen(true);
    setDadosHeader(dadosHeader);
  };

  const handleDeleteButton = useCallback(
    (_, item) => {
      setExcluirModalOpenPreco(true);
      setPrecosRegraData(item);
    },
    [setExcluirModalOpenPreco],
  );

  const onSucessDeletar = useCallback(
    (response) => {
      setLoading(false);

      if (response?.isOffline) {
        closeExcluirModalPreco();
        setResponseDelete({
          isOpen: true,
          message:
            "Envio de exclusão feito offline e será enviada para o servidor assim que a conexão estiver estabelecida com sucesso!",
          link: `${initialPath}/tabela-preco/`,
        });
        return;
      }
      if (Array.isArray(response)) {
        const error = response.find((res) => res.status === "Error");
        if (error) {
          closeExcluirModalPreco();
          setResponseDelete({ isOpen: true, message: error?.mensagem, link: null, status: "warning" });
          return;
        }
      }
      closeExcluirModalPreco();
      setResponseDelete({
        isOpen: true,
        message: "Envio de exclusão feito com sucesso!",
        link: `${initialPath}/tabela-preco/`,
      });
    },
    [closeExcluirModalPreco],
  );

  const onErrorDeletar = useCallback(() => {
    setLoading(false);
    closeExcluirModalPreco();
    setResponseDelete({
      isOpen: true,
      message: "Ocorreu um erro ao tentar deletar preço!",
      link: null,
      status: "warning",
    });
  }, [closeExcluirModalPreco]);

  const excluirPrecoAction = useCallback(async () => {
    if (dadosDelete?.length > 0) {
      const client = new apiGenericClient("simulacao", "/api/PortalAprovacao/DeletarPreco/");
      const response = await client.createData(dadosDelete);
      return response?.data === undefined ? onErrorDeletar() : onSucessDeletar(response.data);
    }
    return {};
  }, [dadosDelete, onErrorDeletar, onSucessDeletar]);

  const handleDelete = useCallback(() => {
    setLoading(true);
    excluirPrecoAction();
  }, [excluirPrecoAction]);

  const onSuccessAlteracao = (response) => {
    setLoading(false);
    clearAll();
    if (response?.isOffline) {
      setIsOpenInformacoesAdicionaisAlteracaoModal(false);
      setShowResponseMsg(
        `Condições comerciais armazenadas offline para portal e serão enviadas para o servidor assim que a conexão estiver estabelecida`,
      );
      setShowResponseAlert(true);
      return;
    }
    if (response?.errosEnvio?.length > 0) {
      setIsOpenInformacoesAdicionaisAlteracaoModal(false);
      setShowFooterButtons(false);
      setModalErros(response.errosEnvio || []);
      setShowResponseAlertError(true);
      if (response?.renovacoesNegociacoes?.length > 0) {
        const codigosExternos = getCodigoExternos(response.renovacoesNegociacoes);
        setShowResponseMsg(`Condições comerciais enviadas com sucesso para portal com motivo de ALTERAÇÃO DE FAIXA ${codigosExternos}`);
        setShowResponseAlert(true);
      }
      return;
    }

    const codigosExternos = getCodigoExternos(response?.renovacoesNegociacoes);
    setIsOpenInformacoesAdicionaisAlteracaoModal(false);
    setShowFooterButtons(false);
    setShowResponseMsg(`Condições comerciais enviadas com sucesso para portal com motivo de ALTERAÇÃO DE FAIXA ${codigosExternos}`);
    setShowResponseAlert(true);
  };

  const onAlteracaoError = (response) => {
    setLoadingSearch(false);
    setShowResponseAlertError(true);
    setIsOpenInformacoesAdicionaisAlteracaoModal(false);
    setShowFooterButtons(false);
    setShowResponseErrorMsg(response.message || 'Ocorreu um erro ao tentar alterar negociação!');
  };

  const handleSubmitAltercao = async (dados) => {
    if ((alteracoes || []).length > 0) {
      dados.alteracoes = alteracoes;
    }

    setLoading(true);
    setIsOpenInformacoesAdicionaisAlteracaoModal(false);

    try {
      const response = await renovarNegociacao(dados);
      if (response.isSuccess != undefined && response.isSuccess === false) onAlteracaoError(response);
      else onSuccessAlteracao(response);
    } finally {
      setLoading(false);
    }
  };

  const onCloseAlert = useCallback(() => {
    const url = id
      ? `${initialPath}/tabela-preco/${id}/solicitacoes-preco`
      : `${initialPath}/tabela-preco/minhas-solicitacoes`;
    navigate(url);
  }, [id, navigate]);

  useConfigureFooter(
    {
      hideHomeButton: false,
      buttonsComponent: isFormDirty ? <TabelaDePrecoSubmit handleSubmit={handleAltualizar} /> : null,
    },
    [isFormDirty]
  );

  useUnsavedChangesWarningTable(isFormDirty, navigate, "Voltar");

  return (
    <div className="client-details-page">
      <FormProvider {...methods}>
        <FiltroPrecoCliente
          nomeDoFiltro={"clientePrice"}
          loadingx={loadingFilters}
          solicitacao={false}
          setAplicaServicos={setAplicaServicos}
          showActions
          filtrosDisponiveis={filtrosDisponiveisPrecoRegra}
          cliente={cliente}
        />

        <ContainerDoPrecoCliente
          cardCliente={true}
          showEditButton={false}
          showAddButton={true}
          groupByClient={false}
          id={parseInt(id)}
          handleAddButton={handleAddButton}
          handleDeleteButton={handleDeleteButton}
          handleChangeCheckbox={handleChangeCheckbox}
          filters={filtersWatch}
          precosCliente={tabelaPrecoClientesFormatada}
          loadingSearch={loadingSearch}
          take={take}
          setSkip={setSkip}
          total={total}
          totalRenderizado={totalRenderizado}
          aplicaServicos={aplicaServicos}
        />

        {isOpenInformacoesAdicionaisAlteracaoModal && (
          <LazyModalWrapper>
            <InformacoesAdicionaisModal
              isOpen={isOpenInformacoesAdicionaisAlteracaoModal}
              onClose={() => setIsOpenInformacoesAdicionaisAlteracaoModal(false)}
              handleSubmit={handleSubmitAltercao}
              submitLabel={"Enviar negociações"}
            />
          </LazyModalWrapper>
        )}

        <ResponseAlert
          fixed={true}
          show={showResponseAlert}
          setShow={setShowResponseAlert}
          text={showResponseMsg}
          onClose={onCloseAlert}
        />

        {showResponseAlertError && modalErros.length > 0 && (
          <ModalErroNegociacao
            isOpen={showResponseAlertError}
            erros={modalErros}
            closeModal={() => { setShowResponseAlertError(false); setModalErros([]); }}
          />
        )}
        {showResponseAlertError && modalErros.length === 0 && (
          <ModalResponse
            isOpen={showResponseAlertError}
            status={"warning"}
            message={showResponseErrorMsg}
            closeModal={() => setShowResponseAlertError(false)}
            link={null}
          />
        )}

        {isExcluirModalOpenPreco && (
          <LazyModalWrapper>
            <ExcluirNegociacao
              isOpen={isExcluirModalOpenPreco}
              onClose={closeExcluirModalPreco}
              data={precosRegraData}
              onConfirm={handleDelete}
              mensagem={`Escolha qual negociação você deseja excluir:`}
              setSelected={setDadosDelete}
              loading={loading}
            />
          </LazyModalWrapper>
        )}

        {responseDelete?.isOpen && (
          <LazyModalWrapper>
            <ModalResponse
              isOpen={responseDelete?.isOpen}
              status={responseDelete?.status || "success"}
              message={responseDelete?.message || "Operação realizada com sucesso!"}
              closeModal={() => setResponseDelete({ isOpen: false, message: "", link: null })}
              link={responseDelete.link ? responseDelete.link : null}
            />
          </LazyModalWrapper>
        )}

        {isModalOpen && (
          <LazyModalWrapper>
            <PricesTableSolicitarNegociacaoModal
              isOpen={isModalOpen}
              closeModal={closeModal}
              dadosHeader={dadosHeader}
            />
          </LazyModalWrapper>
        )}

        {loading && (
          <div className="spinner__form">
            <LoaderFull />
          </div>
        )}
      </FormProvider>
    </div>
  );
};

const ClientPrice = () => {
  return (
    <TableStateProvider>
      <ClientPriceContent />
    </TableStateProvider>
  );
};

export default ClientPrice;
